import { apiFetch, apiPost, apiDelete, type ApiResponse } from './api';

// ============================================================================
// TYPES
// ============================================================================

export interface ComputedBaseline {
  incidentCount30d: number;
  alertSourceDistribution: Record<string, number>;
  closureClassificationDistribution: Record<string, number>;
  medianRiskScore: number;
  p90RiskScore: number;
  topNoisePatterns: {
    title: string;
    count: number;
    closedAsInformational: number;
    informationalRate: number;
  }[];
  fallbackRate: number;
  playbookAccuracy: number;
  avgFeedbackRating: number | null;
  computedAt: string;
}

export interface TenantTuningProfile {
  profileId: string;
  tenantId: string;
  industry: string | null;
  contextNotes: string | null;
  baselineNotes: string | null;
  noisePatterns: { titlePattern: string; riskCeiling: number; note: string }[] | null;
  computedBaseline: ComputedBaseline | null;
  computedAt: string | null;
  isActive: boolean;
}

export interface SignalOverride {
  overrideId: string;
  tenantId: string;
  playbookId: string;
  signalType: string;
  signalValue: string;
  weightOverride: number | null;
  action: 'add' | 'remove' | 'reweight';
  reason: string | null;
  recommendationId: string | null;
  createdBy: string;
  createdAt: string;
}

export interface TuningRecommendation {
  recommendationId: string;
  tenantId: string;
  sourceType: string;
  category: string;
  title: string;
  description: string;
  priority: string;
  status: string;
  createdAt: string;
}

export interface TenantTuningData {
  profile: TenantTuningProfile | null;
  overrides: SignalOverride[];
  recommendations: TuningRecommendation[];
}

export interface AddOverrideRequest {
  playbookId: string;
  signalType: string;
  signalValue: string;
  action: 'add' | 'remove' | 'reweight';
  weightOverride?: number | null;
  reason?: string | null;
}

// ============================================================================
// API CALLS
// ============================================================================

export function getTenantTuning(tenantId: string): Promise<ApiResponse<TenantTuningData>> {
  return apiFetch<TenantTuningData>(`/tuning/${tenantId}`);
}

export function addTenantOverride(
  tenantId: string,
  body: AddOverrideRequest,
): Promise<ApiResponse<SignalOverride>> {
  return apiPost<SignalOverride>(`/tuning/${tenantId}/overrides`, body);
}

export function deleteTenantOverride(
  tenantId: string,
  overrideId: string,
): Promise<ApiResponse<{ deleted: boolean }>> {
  return apiDelete<{ deleted: boolean }>(`/tuning/${tenantId}/overrides/${overrideId}`);
}
